/* eslint-disable no-return-assign */
import React, { useState, useEffect, useRef } from 'react';
import { CircleCheck, ForwardStep } from '@saleshandy/icons';
import type { IProps } from './tasks-content-container';
import Table from '../../../../shared/design-system/components/organisms/table';
import { PaginationShowHide } from '../../../../shared/design-system/components/organisms/table/types';
import {
  executeOnRequestStatus,
  getIsRequestPending,
} from '../../../../shared/utils';
import { tasksTableData } from './utils/tasks-data';
import { getTasksColumns } from './utils/tasks-columns';
import { TaskAction, TaskStatusKey } from '../../utils/enums';
import SnoozeModal from './components/snooze-modal';
import { getTaskStatusParams } from '../../utils/helper';
import ConfirmationModalV3 from '../../../../shared/design-system/components/atoms/confirmation-modal/confirmation-modal-v3';
import { SequenceStepType, SortOrder } from '../../../sequence/enums';
import Select from '../../../../shared/design-system/components/select';
import {
  getCallOutcomeOptions,
  getCallTaskDesc,
  getCompleteActionDesc,
  getConfirmationModalClass,
  getSelectedTasksId,
  getTaskActionTitle,
  getTaskTableClass,
  getTaskTypeFiltersInLocalStore,
  getToasterMessage,
  isAnyCallTaskSelected,
  getBulkActionRequestPayload,
  TaskSortBy,
  isCallTask,
  isCompletedOrSkippedTab,
  formatNumberWithCommas,
  getTasksFiltersFromLocalStore,
  getShouldShowSelectedRowInfoBanner,
  SELECT_ALL_PRODUCTS_LIMIT,
  setSearchFilterInLocalStore,
  getSearchFilterFromLocalStore,
  removeSearchFilterFromLocalStore,
  buildTasksStatusCountParamsFromLocalStorage,
} from './utils/helper';
import EmptyList from '../../helper/empty-list';
import TasksFilterBar from './components/tasks-filter-bar';
import { constants } from '../../../../shared/enums/constants';
import toaster, { Theme } from '../../../../shared/toaster';
import ProspectsFilters from '../../../prospect/components/prospect-list/components/prospects-filters';
import { Filters } from '../../../../shared/types/prospects-filters';
import { checkIsFilterApplied } from '../../../sequence/components/sequence-single-content/components/sequence-single-contacts/components/contact-list-content/helper';
import ProspectsFilterBar from '../../../prospect/components/prospect-list/components/prospects-filters/prospects-filter-bar';
import { resolveFilters } from '../../../prospect/helpers/prospect-filters-resolver';
import ReassignModal from './components/reassign-modal';
import TaskerModal from '../tasker-modal';
import GlobalRestrictionModal from '../../../home/components/global-restriction-modal';
import TasksSelectedRowInfoBanner from './components/tasks-selected-info-banner';
import TaskEmptyList from '../../helper/task-empty-list';

// Prospect Details Components
import ProspectDetails from '../../../prospect/components/prospect-list/components/prospect-details';
import AddToSequenceModal from '../../../prospect/components/prospect-list/components/modals/add-to-sequence-modal';
import ProspectActivityModal from '../../../sequence/components/sequence-single-content/components/sequence-single-contacts/components/contact-list-content/components/prospect-activity-modal/prospect-activity-modal';
import hasPermission from '../../../../shared/utils/access-control/has-permission';
import { Permissions } from '../../../../shared/utils/access-control/enums/permissions';
import TasksFilterBarLoader from './components/tasks-filter-bar/tasks-filter-bar-loader';
import { showingLengthPerPageOptions } from '../../../leads/helpers/helper';
import { StorageUtil } from '../../../../shared/utils/storage';
import { useProspectDetailsFromUrl } from '../../../../shared/hooks/use-prospect-details-from-url';
import FailedReasonPopup from './components/failed-reason-popup';
import BulkRetryModal from './components/bulk-retry-modal';
import { FailedTaskDetail } from '../../../linkedin-automation/types';

const TasksContent: React.FC<IProps> = ({
  taskStatus,
  taskActionRequestStatus,
  bulkTasksUpdateRequestStatus,
  bulkTasksUpdateRequestMessage,
  getUndoTasksRequestStatus,
  tasksFilters,
  selectedTasks,
  updateTasksRequest,
  showLoadingBar,
  hideLoadingBar,
  setTasksFilters,
  resetTasksFilters,
  getTasksStatusCountRequest,
  getTasksRequest,
  resetSelectedTasks,
  removeTaskFromTaskerData,
  isDemoAccount,
  sendGetAllOutcomesRequest,
  checkSingle,
  checkAll,
  sendBulkTasksUpdateRequest,
  outcomeChangeRequestStatus,
  bulkSelectedTasks,
  onBulkSelectTasks,
  clearTasksSelection,
  taskStatusCount,
  sendGetUndoTasksRequest,
  resetBulkTasksState,
  resetUndoTasksState,
  tasks,
  tasksCount,
  getTasksRequestStatus,
  isFirstTimeUser,

  // Prospect Details Props
  addToStepRequestStatus,
  addToStepRequestError,
  addTagsToProspectsRequestStatus,
  saveContactAttributesRequestStatus,
  Fields,

  // Prospect Details Dispatch Props
  sendAddContactToStepRequest,
  resetSequenceAndStepData,
  sendGetSingleProspectDetailsRequest,

  // AddToSequenceModal Dispatch Props
  sendGetSequenceList,

  // Single Prospect Details Props
  selectedProspectId: selectedProspectIdFromState,
  selectedProspectData: selectedProspectDataFromState,
  selectedProspectSequences: selectedProspectSequencesFromState,
  getSingleProspectDetailsStatus,
  getSingleProspectSequencesDetailsStatus,

  // Single Prospect Details Dispatch Props
  setSelectedProspectId,
  resetSelectedProspectId,
  resetSelectedProspectData,

  allOutcomes,
  updateLocalTaskStatusCounts,
  getFieldsRequest,
}) => {
  const tableRef = useRef<any>(null);
  const taskerModalUserInteractedRef = useRef(false);
  const isFailedTab = taskStatus === TaskStatusKey.Failed;
  const [
    failedReasonTask,
    setFailedReasonTask,
  ] = useState<FailedTaskDetail | null>(null);
  const [showBulkRetryModal, setShowBulkRetryModal] = useState(false);
  const [isBulkRetrying, setIsBulkRetrying] = useState(false);

  const [showGlobalRestrictionModal, setShowGlobalRestrictionModal] = useState(
    false,
  );
  const [
    globalRestrictionModalContent,
    setGlobalRestrictionModalContent,
  ] = useState({ title: '', description: '' });

  const [paginationOptions, setPaginationOptions] = useState({
    pageNumber: 1,
    pageLimit: constants.DEFAULT_PAGE_SIZE,
  });
  // Set initial search value from local storage
  const [search, setSearch] = useState<string>(getSearchFilterFromLocalStore());
  const [taskId, setTaskId] = useState<number>(null);
  const [taskAction, setTaskAction] = useState<TaskAction>(null);
  const [taskType, setTaskType] = useState<SequenceStepType>(null);
  const [callOutcome, setCallOutcome] = useState<{
    key: string;
    label: string;
  }>(null);
  const [extraPayload, setExtraPayload] = useState<any>(null);
  const [taskOpenProspectId, setTaskOpenProspectId] = useState<number>(null);
  const [isBulkTasksSelected, setIsBulkTasksSelected] = useState<boolean>(
    false,
  );

  const [
    isSnoozeConfirmationModalVisible,
    setIsSnoozeConfirmationModalVisible,
  ] = useState<boolean>(false);
  const [
    isConfirmationModalVisible,
    setIsConfirmationModalVisible,
  ] = useState<boolean>(false);
  const [
    isTaskFilterModalVisible,
    setIsTaskFilterModalVisible,
  ] = useState<boolean>(false);
  const [selectedTaskTypeIds, setSelectedTaskTypeIds] = useState<string>(
    getTaskTypeFiltersInLocalStore()?.join(',') || '',
  );
  const [isReassignModalVisible, setIsReassignModalVisible] = useState<boolean>(
    false,
  );

  const [isTaskerModalVisible, setTaskerModalVisible] = useState<boolean>(
    false,
  );
  const [shareableHashId, setShareableHashId] = useState<string>(null);

  const [activeTabCount, setActiveTabCount] = useState<string>();

  const [sortKey, setSortKey] = useState<TaskSortBy>(null);
  const [sortOrder, setSortOrder] = useState<SortOrder>(null);

  const [copiedRows, setCopiedRows] = useState<Set<number>>(new Set());

  // Add state to track hovered row
  const [hoveredRowId, setHoveredRowId] = useState<number | null>(null);
  const hoveredRowRef = useRef<number | null>(null);

  const [
    shouldRefreshTasksAfterModalHide,
    setShouldRefreshTasksAfterModalHide,
  ] = useState<boolean>(false);

  const [outcomeChangePayload, setOutcomeChangePayload] = useState<{
    sequenceId: number;
    prospectId: number;
    outcomeId: number;
  } | null>(null);

  const [snoozeDate, setSnoozeDate] = useState<Date>(null);

  // Add local state for tasks to enable local updates
  const [localTasks, setLocalTasks] = useState(tasks);

  // Add ref to track initial render to prevent duplicate API calls
  const isInitialRenderRef = useRef(true);
  const prevTasksFiltersRef = useRef(tasksFilters);

  // Add ref to track the debounced count API timer
  const countApiTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Add local state for task status counts to enable local updates
  const [localTaskStatusCount, setLocalTaskStatusCount] = useState(
    taskStatusCount,
  );

  // Prospect Details State
  const [
    showProspectDetailsModal,
    setShowProspectDetailsModal,
  ] = useState<boolean>(false);
  const [showAddToSequenceModal, setShowAddToSequenceModal] = useState<boolean>(
    false,
  );
  const [
    showAddToSequenceReportModal,
    setShowAddToSequenceReportModal,
  ] = useState<boolean>(false);
  const [prospectActivityModalState, setProspectActivityModalState] = useState<{
    showEngagementStatsModal: boolean;
    activeProspectId: string;
    activeType: string;
    prospectName: string;
    prospectEmail: string;
  }>({
    showEngagementStatsModal: false,
    activeProspectId: null,
    activeType: null,
    prospectName: '',
    prospectEmail: '',
  });

  // Synchronously reset sortKey and sortOrder when taskStatus changes
  const prevTaskStatusRef = useRef(taskStatus);
  if (prevTaskStatusRef.current !== taskStatus) {
    if (sortKey !== null) setSortKey(null);
    if (sortOrder !== null) setSortOrder(null);
  }

  // Ref to track search input
  const prevSearchValueRef = useRef(search);
  const isFirstTabChange = useRef(true);
  const confirmedSearchValueRef = useRef(search);

  const filters = resolveFilters(tasksFilters);

  const getLimitedTaskCount = () => {
    const totalTaskCount = Number(activeTabCount);
    return totalTaskCount > SELECT_ALL_PRODUCTS_LIMIT
      ? SELECT_ALL_PRODUCTS_LIMIT
      : totalTaskCount;
  };

  // Create tasks object with the expected structure from Redux state
  const tasksData = { data: localTasks, isFirstTimeUser, count: tasksCount };

  // Helper function to get filtered selected tasks count
  const getFilteredSelectedTasksCount = () => {
    // If filters are applied, filter the selected tasks to only include those that match current criteria
    if (filters && tasksData?.data && selectedTasks?.length > 0) {
      const currentTaskIds = new Set(tasksData?.data?.map((task) => task?.id));
      const filteredSelectedTasks = selectedTasks?.filter((task) =>
        currentTaskIds?.has(task.id),
      );
      return filteredSelectedTasks?.length;
    }
    return selectedTasks?.length;
  };

  // Helper function to check if current page is within the select all limit
  const isCurrentPageUnderTheSelectAllLimit = () => {
    const lastPage = SELECT_ALL_PRODUCTS_LIMIT / paginationOptions.pageLimit;
    return (
      bulkSelectedTasks?.isAllTasksSelected &&
      lastPage >= paginationOptions.pageNumber
    );
  };

  const shouldSelectAllCurrentPageRows = () => {
    const lastPage = SELECT_ALL_PRODUCTS_LIMIT / paginationOptions.pageLimit;
    return (
      Number(activeTabCount) <= SELECT_ALL_PRODUCTS_LIMIT ||
      lastPage > paginationOptions.pageNumber
    );
  };

  const ALL_TASK_TYPE_IDS = '2,3,4,5,6,9,11,12,13,14,15,16,17,18';

  const params = {
    ...(filters?.sequences && { sequenceIds: filters?.sequences }),
    ...(filters?.owners && { owners: filters?.owners }),
    ...(filters?.['created date'] && {
      dueDate: filters?.['created date'],
    }),
    ...(filters?.outcomes && { outcomes: filters?.outcomes }),
    ...(filters?.callOutcomes && { callOutcomes: filters?.callOutcomes }),
    ...(filters?.['task priority'] && { priority: filters?.['task priority'] }),
    ...(search && { search }),
    ...(selectedTaskTypeIds &&
      selectedTaskTypeIds.length > 0 &&
      selectedTaskTypeIds !== ALL_TASK_TYPE_IDS && {
        taskType: selectedTaskTypeIds,
      }),
    ...(sortKey && { sortBy: sortKey }),
    ...(sortOrder && { sortOrder: sortOrder.toUpperCase() }),
    ...(filters?.subSequenceIds && {
      subSequenceId: filters?.subSequenceIds,
    }),
  };

  const onRefresh = () => {
    getTasksRequest({
      status: getTaskStatusParams(taskStatus),
      pageLimit: paginationOptions.pageLimit,
      pageNumber: paginationOptions.pageNumber,
      ...params,
    });
    getTasksStatusCountRequest({ ...params });
    resetSelectedTasks();
  };

  const paginationChange = ({ page, limit }) => {
    setPaginationOptions({
      pageNumber: page,
      pageLimit: limit,
    });

    if (
      paginationOptions.pageLimit !== limit ||
      paginationOptions.pageNumber !== page
    ) {
      setTimeout(() => {
        getTasksRequest({
          status: getTaskStatusParams(taskStatus),
          pageLimit: limit,
          pageNumber: page,
          ...params,
        });
      }, 100);
    }
  };
  const updateTasks = (
    tId: number,
    tAction: TaskAction,
    tType: SequenceStepType,
    ePayload = null,
    resetOutcome = true,
  ) => {
    // Determine if this is a hover action (not from checkbox selection)
    const isHoverAction =
      selectedTasks.length === 0 ||
      !selectedTasks.some((task) => task.id === tId);
    if (isHoverAction) {
      resetSelectedTasks();
      clearTasksSelection();
    }

    if (isDemoAccount) {
      if (tAction === TaskAction.SkipTask) {
        setGlobalRestrictionModalContent({
          title: 'Skipping tasks is disabled in demo mode',
          description:
            'This is a demo account. Signup to manage your real tasks in Saleshandy.',
        });
        setShowGlobalRestrictionModal(true);
        return;
      }
      if (tAction === TaskAction.SnoozeTask) {
        setGlobalRestrictionModalContent({
          title: 'Snoozing tasks is disabled in demo mode',
          description:
            'This is a demo account. Signup to snooze tasks and manage your follow-ups effectively.',
        });
        setShowGlobalRestrictionModal(true);
        return;
      }
      if (tAction === TaskAction.ExecuteTask) {
        setGlobalRestrictionModalContent({
          title: 'Completing tasks is disabled in demo mode',
          description:
            'This is a demo account. Signup to track and complete real tasks in Saleshandy.',
        });
        setShowGlobalRestrictionModal(true);
        return;
      }
    }

    if (
      tAction === TaskAction.SkipTask &&
      bulkSelectedTasks?.isAllTasksSelected
    ) {
      setTaskId(tId);
      setTaskAction(tAction);
      setTaskType(tType);
      setExtraPayload(ePayload);
      setIsConfirmationModalVisible(true);
      return;
    }

    const isFromTaskerModal = isTaskerModalVisible && taskId;

    if (isFromTaskerModal) {
      // For snooze action, show snooze modal instead of direct API call
      if (tAction === TaskAction.SnoozeTask) {
        setTaskId(tId);
        setTaskAction(tAction);
        setTaskType(tType);
        setExtraPayload(ePayload);
        setIsSnoozeConfirmationModalVisible(true);
        return;
      }

      // For Call tasks with execute action, show modal instead of direct API call unless callOutcome is already selected
      if (
        tAction === TaskAction.ExecuteTask &&
        isCallTask(tType) &&
        !ePayload?.callOutcome?.key
      ) {
        setTaskId(tId);
        setTaskAction(tAction);
        setTaskType(tType);
        setExtraPayload(ePayload);

        if (ePayload?.callOutcome) {
          setCallOutcome(ePayload.callOutcome?.key);
        }

        setIsConfirmationModalVisible(true);
        return;
      }

      // For non-call tasks with execute action, proceed directly without showing confirmation modal
      if (tAction === TaskAction.ExecuteTask && !isCallTask(tType)) {
        setTaskAction(tAction);
        setTaskType(tType);
        setExtraPayload(ePayload);

        const payload = {
          taskIds: [taskId],
          taskAction: tAction,
          ...(ePayload && { ...ePayload }),
        };

        setPaginationOptions({
          pageLimit: paginationOptions.pageLimit,
          pageNumber:
            paginationOptions.pageNumber > 1 && tasksData.data?.length === 1
              ? paginationOptions.pageNumber - 1
              : paginationOptions.pageNumber,
        });

        updateTasksRequest(payload);
        return;
      }

      setTaskAction(tAction);
      setTaskType(tType);
      setExtraPayload(ePayload);

      const shouldSendCallOutcome =
        (tAction === TaskAction.ExecuteTask ||
          tAction === TaskAction.SkipTask) &&
        isCallTask(tType) &&
        !!ePayload?.callOutcome?.key;

      const { callOutcome: extraPayloadCallOutcome, ...restOfExtraPayload } =
        ePayload || {};

      const payload = {
        taskIds: [taskId],
        taskAction: tAction,
        ...(shouldSendCallOutcome && {
          callOutcome: ePayload?.callOutcome?.key,
        }),
        ...restOfExtraPayload,
      };

      if (extraPayloadCallOutcome) {
        payload.callOutcome = extraPayloadCallOutcome?.key;
      }

      setPaginationOptions({
        pageLimit: paginationOptions.pageLimit,
        pageNumber:
          paginationOptions.pageNumber > 1 && tasksData.data?.length === 1
            ? paginationOptions.pageNumber - 1
            : paginationOptions.pageNumber,
      });

      updateTasksRequest(payload);
      return;
    }

    const isSingleTask =
      selectedTasks.length === 0 || selectedTasks.length === 1;

    // For single tasks with skip actions, directly call the API except for call tasks
    if (isSingleTask && tAction === TaskAction.SkipTask) {
      setTaskId(tId);
      setTaskAction(tAction);
      setTaskType(tType);
      setExtraPayload(ePayload);

      const selectedTaskIds = getSelectedTasksId(selectedTasks);
      // eslint-disable-next-line no-nested-ternary
      const taskIdsToUse = isHoverAction
        ? [tId]
        : selectedTaskIds?.length
        ? selectedTaskIds
        : [tId];

      const shouldSendCallOutcome =
        isCallTask(tType) && !!ePayload?.callOutcome?.key;

      const { callOutcome: extraPayloadCallOutcome, ...restOfExtraPayload } =
        ePayload || {};

      const payload = {
        taskIds: taskIdsToUse,
        taskAction: tAction,
        ...(shouldSendCallOutcome && {
          callOutcome: ePayload?.callOutcome?.key,
        }),
        ...restOfExtraPayload,
      };

      if (extraPayloadCallOutcome) {
        payload.callOutcome = extraPayloadCallOutcome?.key;
      }

      setPaginationOptions({
        pageLimit: paginationOptions.pageLimit,
        pageNumber:
          paginationOptions?.pageNumber > 1 && tasksData.data?.length === 1
            ? paginationOptions.pageNumber - 1
            : paginationOptions.pageNumber,
      });

      updateTasksRequest(payload);
      return;
    }

    // For RetryLinkedInTask, directly call the API without opening a modal
    if (tAction === TaskAction.RetryLinkedInTask) {
      setTaskId(tId);
      setTaskAction(tAction);
      setTaskType(tType);

      const payload = {
        taskIds: [tId],
        taskAction: tAction,
      };

      setPaginationOptions({
        pageLimit: paginationOptions.pageLimit,
        pageNumber:
          paginationOptions.pageNumber > 1 && tasksData.data?.length === 1
            ? paginationOptions.pageNumber - 1
            : paginationOptions.pageNumber,
      });

      updateTasksRequest(payload);
      return;
    }

    // For single tasks with execute actions, only show modal for call tasks
    if (
      isSingleTask &&
      tAction === TaskAction.ExecuteTask &&
      !isCallTask(tType)
    ) {
      setTaskId(tId);
      setTaskAction(tAction);
      setTaskType(tType);
      setExtraPayload(ePayload);

      const selectedTaskIds = getSelectedTasksId(selectedTasks);
      // eslint-disable-next-line no-nested-ternary
      const taskIdsToUse = isHoverAction
        ? [tId]
        : selectedTaskIds?.length
        ? selectedTaskIds
        : [tId];

      const payload = {
        taskIds: taskIdsToUse,
        taskAction: tAction,
        ...(ePayload && { ...ePayload }),
      };

      setPaginationOptions({
        pageLimit: paginationOptions.pageLimit,
        pageNumber:
          paginationOptions.pageNumber > 1 && tasksData.data?.length === 1
            ? paginationOptions.pageNumber - 1
            : paginationOptions.pageNumber,
      });

      updateTasksRequest(payload);
      return;
    }

    setTaskId(tId);
    setTaskAction(tAction);
    setTaskType(tType);
    setExtraPayload(ePayload);

    if (tAction === TaskAction.SnoozeTask) {
      setIsSnoozeConfirmationModalVisible(true);
      return;
    }

    // For Call tasks, extract and set callOutcome from ePayload if available
    if (isCallTask(tType) && ePayload?.callOutcome) {
      setCallOutcome(ePayload.callOutcome);
    }

    // Don't reset callOutcome for call tasks when opening the modal
    if (resetOutcome && !isCallTask(tType)) {
      setCallOutcome(null);
    }
    setIsConfirmationModalVisible(true);
  };

  const onTaskClick = (id: number, row?: any) => {
    setTaskId(id);
    setTaskerModalVisible(true);
    setTaskOpenProspectId(row?.prospectId);
  };

  const onSnooze = async (date) => {
    // Set snooze date for undo functionality
    setSnoozeDate(date);
    const isFromTaskerModal = isTaskerModalVisible && taskId;

    if (isFromTaskerModal) {
      setTaskAction(TaskAction.SnoozeTask);
      setTaskType(taskType);
      setExtraPayload(extraPayload);

      const payload = {
        taskIds: [taskId], // Always use modal's taskId
        rescheduledDate: date?.toString(),
        taskAction: TaskAction.SnoozeTask,
        ...(extraPayload?.callOutcome && {
          callOutcome: extraPayload?.callOutcome?.key,
        }),
      };

      setPaginationOptions({
        pageLimit: paginationOptions.pageLimit,
        pageNumber:
          paginationOptions.pageNumber > 1 && tasksData.data?.length === 1
            ? paginationOptions.pageNumber - 1
            : paginationOptions.pageNumber,
      });

      await updateTasksRequest(payload);
      return;
    }

    const selectedTaskIds = getSelectedTasksId(selectedTasks);
    const taskIdsToUse = selectedTaskIds?.length ? selectedTaskIds : [taskId];

    const payload = {
      taskIds: taskIdsToUse,
      rescheduledDate: date?.toString(),
      taskAction: TaskAction.SnoozeTask,
      ...(extraPayload?.callOutcome && {
        callOutcome: extraPayload?.callOutcome?.key,
      }),
    };

    setPaginationOptions({
      pageLimit: paginationOptions.pageLimit,
      pageNumber:
        paginationOptions.pageNumber > 1 && tasksData.data?.length === 1
          ? paginationOptions.pageNumber - 1
          : paginationOptions.pageNumber,
    });

    if (bulkSelectedTasks?.isAllTasksSelected) {
      // Use bulk API for all tasks
      const bulkPayload = {
        ...getBulkActionRequestPayload(bulkSelectedTasks, selectedTasks),
        rescheduledDate: date?.toString(),
        taskAction: TaskAction.SnoozeTask,
        ...(extraPayload?.callOutcome && {
          callOutcome: extraPayload?.callOutcome?.key,
        }),
      };
      await sendBulkTasksUpdateRequest(bulkPayload);
      getTasksRequest({
        status: getTaskStatusParams(taskStatus),
        pageLimit: paginationOptions.pageLimit,
        pageNumber: paginationOptions.pageNumber,
        ...params,
      });
    } else {
      await updateTasksRequest(payload);
      // Only call getTasksRequest for multiple tasks, not single tasks
      if (selectedTasks.length > 1) {
        getTasksRequest({
          status: getTaskStatusParams(taskStatus),
          pageLimit: paginationOptions.pageLimit,
          pageNumber: paginationOptions.pageNumber,
          ...params,
        });
      }
    }
  };

  const onConfirm = async () => {
    // Check if this is called from tasker modal
    const isFromTaskerModal = isTaskerModalVisible && taskId;

    if (isFromTaskerModal) {
      setTaskAction(taskAction);
      setTaskType(taskType);
      setExtraPayload(extraPayload);

      const shouldSendCallOutcome =
        (taskAction === TaskAction.ExecuteTask ||
          taskAction === TaskAction.SkipTask) &&
        isCallTask(taskType) &&
        !!callOutcome?.key;

      const { callOutcome: extraPayloadCallOutcome, ...restOfExtraPayload } =
        extraPayload || {};

      const payload = {
        taskIds: [taskId],
        taskAction,
        ...(shouldSendCallOutcome && { callOutcome: callOutcome?.key }),
        ...restOfExtraPayload,
      };

      // Don't override callOutcome if it's already set from shouldSendCallOutcome
      if (extraPayloadCallOutcome && !shouldSendCallOutcome) {
        payload.callOutcome = extraPayloadCallOutcome?.key;
      }

      setPaginationOptions({
        pageLimit: paginationOptions.pageLimit,
        pageNumber:
          paginationOptions.pageNumber > 1 && tasksData.data?.length === 1
            ? paginationOptions.pageNumber - 1
            : paginationOptions.pageNumber,
      });

      await updateTasksRequest(payload);
      return;
    }

    const selectedTaskIds = getSelectedTasksId(selectedTasks);
    const taskIdsToUse = selectedTaskIds?.length ? selectedTaskIds : [taskId];

    const shouldSendCallOutcome =
      (taskAction === TaskAction.ExecuteTask ||
        taskAction === TaskAction.SkipTask) &&
      isCallTask(taskType) &&
      !!callOutcome?.key;

    const { callOutcome: extraPayloadCallOutcome, ...restOfExtraPayload } =
      extraPayload || {};

    const payload = {
      taskIds: taskIdsToUse,
      taskAction,
      callOutcome: callOutcome?.key,
      ...restOfExtraPayload,
    };

    // Don't override callOutcome if it's already set from shouldSendCallOutcome
    if (extraPayloadCallOutcome && !shouldSendCallOutcome) {
      payload.callOutcome = extraPayloadCallOutcome?.key;
    }

    setPaginationOptions({
      pageLimit: paginationOptions.pageLimit,
      pageNumber:
        paginationOptions.pageNumber > 1 && tasksData.data?.length === 1
          ? paginationOptions.pageNumber - 1
          : paginationOptions.pageNumber,
    });

    if (bulkSelectedTasks?.isAllTasksSelected) {
      // Use bulk API for all tasks
      const bulkPayload = {
        ...getBulkActionRequestPayload(bulkSelectedTasks, selectedTasks),
        taskAction,
        ...((taskAction === TaskAction.ExecuteTask ||
          taskAction === TaskAction.SkipTask) &&
          isCallTask(taskType) && {
            callOutcome: callOutcome?.key,
          }),
      };

      if (extraPayloadCallOutcome && !shouldSendCallOutcome) {
        bulkPayload.callOutcome = extraPayloadCallOutcome?.key;
      }
      await sendBulkTasksUpdateRequest(bulkPayload);
      getTasksRequest({
        status: getTaskStatusParams(taskStatus),
        pageLimit: paginationOptions.pageLimit,
        pageNumber: paginationOptions.pageNumber,
        ...params,
      });
    } else {
      await updateTasksRequest(payload);
      // Only call getTasksRequest for multiple tasks, not single tasks
      if (selectedTasks.length > 1) {
        getTasksRequest({
          status: getTaskStatusParams(taskStatus),
          pageLimit: paginationOptions.pageLimit,
          pageNumber: paginationOptions.pageNumber,
          ...params,
        });
      }
    }
    setTaskId(null);
  };

  //! Bulk Actions
  const onReassign = async (reAssigneeId: number) => {
    // Check if this is called from tasker modal
    const isFromTaskerModal = isTaskerModalVisible && taskId;

    if (isFromTaskerModal) {
      setTaskAction(taskAction);
      setTaskType(taskType);
      setExtraPayload(extraPayload);

      const payload = {
        taskIds: [taskId],
        newAssigneeId: reAssigneeId,
        taskAction,
      };

      await updateTasksRequest(payload);
      return;
    }

    // For non-modal actions, use selected tasks or current taskId
    const selectedTaskIds = getSelectedTasksId(selectedTasks);
    const taskIdsToUse = selectedTaskIds?.length ? selectedTaskIds : [taskId];

    const payload = {
      taskIds: taskIdsToUse,
      newAssigneeId: reAssigneeId,
      taskAction,
    };

    if (bulkSelectedTasks?.isAllTasksSelected) {
      // Use bulk API for all tasks
      const bulkPayload = {
        ...getBulkActionRequestPayload(bulkSelectedTasks),
        newAssigneeId: reAssigneeId,
        taskAction,
      };
      await sendBulkTasksUpdateRequest(bulkPayload);
      getTasksRequest({
        status: getTaskStatusParams(taskStatus),
        pageLimit: paginationOptions.pageLimit,
        pageNumber: paginationOptions.pageNumber,
        ...params,
      });
    } else {
      await updateTasksRequest(payload);
      // Only call getTasksRequest for multiple tasks, not single tasks
      if (selectedTasks.length > 1) {
        getTasksRequest({
          status: getTaskStatusParams(taskStatus),
          pageLimit: paginationOptions.pageLimit,
          pageNumber: paginationOptions.pageNumber,
          ...params,
        });
      }
    }
  };

  const handleBulkRetry = async () => {
    setIsBulkRetrying(true);
    try {
      if (bulkSelectedTasks?.isAllTasksSelected) {
        const bulkPayload = {
          ...getBulkActionRequestPayload(bulkSelectedTasks, selectedTasks),
          taskAction: TaskAction.RetryLinkedInTask,
        };
        await sendBulkTasksUpdateRequest(bulkPayload);
        // Cleanup and toast are handled by the bulkTasksUpdateRequestStatus useEffect
      } else {
        const taskIds = getSelectedTasksId(selectedTasks);
        if (taskIds.length) {
          await updateTasksRequest({
            taskIds,
            taskAction: TaskAction.RetryLinkedInTask,
          });
          toaster.success('LinkedIn tasks retried successfully', {
            theme: Theme.New,
          });
        }
        resetSelectedTasks();
        clearTasksSelection();
        resetBulkTasksState();
      }
      getTasksRequest({
        status: getTaskStatusParams(taskStatus),
        pageLimit: paginationOptions.pageLimit,
        pageNumber: paginationOptions.pageNumber,
      });
    } finally {
      setIsBulkRetrying(false);
      setShowBulkRetryModal(false);
    }
  };

  const onAction = (action: TaskAction) => {
    if (action === TaskAction.RetryLinkedInTask) {
      setShowBulkRetryModal(true);
      return;
    }

    const isSingleTask =
      selectedTasks.length === 0 || selectedTasks.length === 1;

    // For single tasks with skip or execute actions, directly call updateTasks except for call tasks
    if (
      isSingleTask &&
      (action === TaskAction.SkipTask || action === TaskAction.ExecuteTask) &&
      !(
        action === TaskAction.ExecuteTask &&
        isCallTask(selectedTasks[0]?.taskTitle?.type)
      )
    ) {
      // Get the task to update - either the selected task or use a default taskId
      const taskToUpdate = selectedTasks[0] || { id: null };

      // Call updateTasks directly for single tasks with callOutcome if available
      updateTasks(
        taskToUpdate.id,
        action,
        taskToUpdate.taskTitle?.type,
        null,
        true,
      );
      return;
    }

    setTaskAction(action);

    if (action === TaskAction.SnoozeTask) {
      setIsSnoozeConfirmationModalVisible(true);
      return;
    }
    if (action === TaskAction.ReassignTask) {
      setIsReassignModalVisible(true);
      return;
    }

    setIsConfirmationModalVisible(true);
  };

  const allSelected = (rows: any, status: boolean) => {
    if (bulkSelectedTasks?.isAllTasksSelected) {
      // Only allow selection/deselection if current page is within the limit
      if (!shouldSelectAllCurrentPageRows()) {
        return;
      }

      if (!status) {
        // Tasks are being deselected, add to deselectedTaskIds
        const currentDeselectedIds = bulkSelectedTasks.deselectedTaskIds || [];
        const rowIds = rows.map((row) => row.id);
        onBulkSelectTasks({
          ...bulkSelectedTasks,
          deselectedTaskIds: [...currentDeselectedIds, ...rowIds],
        });
      } else {
        // Tasks are being reselected, remove from deselectedTaskIds
        const currentDeselectedIds = bulkSelectedTasks.deselectedTaskIds || [];
        const rowIds = rows.map((row) => row.id);
        onBulkSelectTasks({
          ...bulkSelectedTasks,
          deselectedTaskIds: currentDeselectedIds.filter(
            (id) => !rowIds.includes(id),
          ),
        });
      }
      return;
    }

    resetSelectedTasks();
    clearTasksSelection();

    checkAll({
      rows,
      status,
    });
    setIsBulkTasksSelected(false);
  };

  const singleSelected = (row, status: boolean) => {
    if (bulkSelectedTasks?.isAllTasksSelected) {
      // Only allow selection/deselection if current page is within the limit
      if (!shouldSelectAllCurrentPageRows()) {
        return;
      }

      if (!status) {
        // Task is being deselected, add to deselectedTaskIds
        const currentDeselectedIds = bulkSelectedTasks.deselectedTaskIds || [];
        onBulkSelectTasks({
          ...bulkSelectedTasks,
          deselectedTaskIds: [...currentDeselectedIds, row.id],
        });
      } else {
        // Task is being reselected, remove from deselectedTaskIds
        const currentDeselectedIds = bulkSelectedTasks.deselectedTaskIds || [];
        onBulkSelectTasks({
          ...bulkSelectedTasks,
          deselectedTaskIds: currentDeselectedIds.filter((id) => id !== row.id),
        });
      }
      return;
    }

    checkSingle({ row, status });
    setIsBulkTasksSelected(false);
  };

  //! Tasks Filter
  const toggleFilter = () =>
    setIsTaskFilterModalVisible(!isTaskFilterModalVisible);
  const hideFilterModal = () => setIsTaskFilterModalVisible(false);

  const getTasksAfterFilter = () => {
    resolveFilters(tasksFilters);
    resetSelectedTasks();
    clearTasksSelection();
  };
  const onClearFilter = () => {
    getTasksRequest({
      status: getTaskStatusParams(taskStatus),
      pageLimit: paginationOptions.pageLimit,
      pageNumber: paginationOptions.pageNumber,
      ...params,
    });
    resetSelectedTasks();
    clearTasksSelection();
  };
  const onTaskTypeFilterApply = (selectedIds: number[]) => {
    setSelectedTaskTypeIds(selectedIds.join(','));
    // Only send taskType if not all are selected
    const apiParams = { ...params };
    if (
      selectedIds.length > 0 &&
      selectedIds.length < ALL_TASK_TYPE_IDS.length
    ) {
      apiParams.taskType = selectedIds.join(',');
    } else if (selectedIds.length === ALL_TASK_TYPE_IDS.length) {
      // If all tasks are selected, don't send taskType
      delete apiParams.taskType;
    } else {
      // If no tasks are selected, don't send taskType
      delete apiParams.taskType;
    }

    getTasksStatusCountRequest(apiParams);
    setTimeout(() => {
      getTasksRequest({
        status: getTaskStatusParams(taskStatus),
        pageLimit: paginationOptions.pageLimit,
        pageNumber: (paginationOptions.pageNumber = 1),
        ...apiParams,
      });
      tableRef?.current?.resetPagination();
    }, 100);

    resetSelectedTasks();
    clearTasksSelection();
  };
  const handleSearch = (searchValue: string) => {
    setSearch(searchValue);
    setSearchFilterInLocalStore(searchValue);
    getTasksStatusCountRequest({ ...params, search: searchValue });
    setTimeout(() => {
      getTasksRequest({
        status: getTaskStatusParams(taskStatus),
        pageLimit: paginationOptions.pageLimit,
        pageNumber: (paginationOptions.pageNumber = 1),
        ...params,
        search: searchValue,
      });
      tableRef?.current?.resetPagination();
    }, 100);
    confirmedSearchValueRef.current = searchValue;

    resetSelectedTasks();
    clearTasksSelection();
  };

  const handleClearSearch = () => {
    setSearch('');
    removeSearchFilterFromLocalStore();
    // Use latest filters, prefer localStorage if present
    const localStorageFilters = getTasksFiltersFromLocalStore();
    let filtersToUse = filters;
    if (localStorageFilters) {
      filtersToUse = resolveFilters(localStorageFilters);
    }

    const statusFromUrl = new URLSearchParams(window?.location?.search)?.get(
      'status',
    );
    const statusKey = (Object?.values(TaskStatusKey) as string[])?.includes(
      statusFromUrl,
    )
      ? (statusFromUrl as TaskStatusKey)
      : TaskStatusKey.DueToday;
    const statusParam = getTaskStatusParams(statusKey);
    const paramsWithLocalFilters = {
      ...(filtersToUse?.sequences && { sequenceIds: filtersToUse?.sequences }),
      ...(filtersToUse?.owners && { owners: filtersToUse?.owners }),
      ...(filtersToUse?.['created date'] && {
        dueDate: filtersToUse?.['created date'],
      }),
      ...(filtersToUse?.['task priority'] && {
        priority: filtersToUse?.['task priority'],
      }),
      ...(filtersToUse?.outcomes && { outcomes: filtersToUse?.outcomes }),
      ...(filtersToUse?.callOutcomes && {
        callOutcomes: filtersToUse?.callOutcomes,
      }),
      search: '',
      ...(selectedTaskTypeIds &&
        selectedTaskTypeIds.length > 0 &&
        selectedTaskTypeIds !== ALL_TASK_TYPE_IDS && {
          taskType: selectedTaskTypeIds,
        }),
      ...(sortKey && { sortBy: sortKey }),
      ...(sortOrder && { sortOrder: sortOrder.toUpperCase() }),
      ...(filtersToUse?.subSequenceIds && {
        subSequenceId: filtersToUse?.subSequenceIds,
      }),
      status: statusParam,
    };
    getTasksStatusCountRequest(paramsWithLocalFilters);
    setTimeout(() => {
      getTasksRequest({
        status: statusParam,
        pageLimit: paginationOptions.pageLimit,
        pageNumber: paginationOptions.pageNumber,
        ...paramsWithLocalFilters,
      });
    }, 100);
    confirmedSearchValueRef.current = '';

    resetSelectedTasks();
    clearTasksSelection();
  };

  const handleClearSelection = (resetSelectedRows = false) => {
    resetSelectedTasks();
    clearTasksSelection();

    // Only clear current page selection if not in bulk mode
    if (!bulkSelectedTasks?.isAllTasksSelected) {
      allSelected(tasksData.data || [], false);
    }

    if (resetSelectedRows || isCurrentPageUnderTheSelectAllLimit()) {
      tableRef.current?.resetSelectedRows();
    }
  };

  const handleSelectAllProspect = () => {
    const appliedFilters = {
      ...params,
      status: getTaskStatusParams(taskStatus),
      ...(search && { search }),
      ...(selectedTaskTypeIds &&
        selectedTaskTypeIds.length > 0 && { taskType: selectedTaskTypeIds }),
    };

    // Set bulk selection for all tasks across all pages with limit
    onBulkSelectTasks({
      isAllTasksSelected: true,
      tasksFilter: appliedFilters,
      deselectedTaskIds: [],
    });
    setIsBulkTasksSelected(true);
  };

  const onSort = (key: TaskSortBy, order: SortOrder) => {
    handleClearSelection(true);
    setSortKey(key);
    setSortOrder(order.toUpperCase() as SortOrder);
  };

  // Add row hover handlers
  const handleRowMouseEnter = (rowId: number) => {
    // Immediately clear any previous hover state and set new one
    hoveredRowRef.current = rowId;
    setHoveredRowId(rowId);
  };

  const handleRowMouseLeave = () => {
    hoveredRowRef.current = null;
    setHoveredRowId(null);
  };

  // Use the reusable hook to handle URL-based prospect details modal
  const {
    selectedProspectId: selectedProspectIdFromUrl,
    showProspectDetailsModal: showProspectDetailsModalFromUrl,
    defaultTab,
    hideShowProspectDetailsModal: hideShowProspectDetailsModalFromUrl,
  } = useProspectDetailsFromUrl({
    // sendGetSingleProspectDetailsRequest,
    resetSelectedProspectId,
    pathnamePattern: /\/tasks\/(\d+)/,
  });

  // Sync the hook's state with component's existing state
  useEffect(() => {
    if (selectedProspectIdFromUrl !== selectedProspectIdFromState) {
      if (selectedProspectIdFromUrl) {
        setSelectedProspectId(selectedProspectIdFromUrl);
      }
    }
  }, [
    selectedProspectIdFromUrl,
    selectedProspectIdFromState,
    setSelectedProspectId,
  ]);

  useEffect(() => {
    if (showProspectDetailsModalFromUrl !== showProspectDetailsModal) {
      setShowProspectDetailsModal(showProspectDetailsModalFromUrl);
      if (Fields?.length === 0) {
        getFieldsRequest();
      }
    }
  }, [
    showProspectDetailsModalFromUrl,
    showProspectDetailsModal,
    setShowProspectDetailsModal,
    Fields,
  ]);

  // Prospect Details Handlers
  const viewShowProspectDetailsModal = (id: number) => {
    if (!hasPermission(Permissions.PROSPECT_READ)) {
      return;
    }
    if (id) {
      setSelectedProspectId(id);
      setShowProspectDetailsModal(true);
    }
  };

  const hideShowProspectDetailsModal = () => {
    hideShowProspectDetailsModalFromUrl();
    resetSelectedProspectData();
    resetSelectedProspectId();

    // If prospect details were updated, refresh the tasks list
    if (shouldRefreshTasksAfterModalHide) {
      getTasksRequest({
        status: getTaskStatusParams(taskStatus),
        pageLimit: paginationOptions.pageLimit,
        pageNumber: paginationOptions.pageNumber,
        ...params,
      });
      setShouldRefreshTasksAfterModalHide(false);
    }
  };

  const resetSelectedId = () => {
    resetSelectedProspectData();
    resetSelectedProspectId();
  };

  const hideEngagementStatisticsPopover = () => {
    setProspectActivityModalState({
      ...prospectActivityModalState,
      showEngagementStatsModal: false,
    });
  };

  const hideAddToSequenceModal = () => {
    setShowAddToSequenceModal(false);
  };

  const onShowAddToSequenceReportModal = () => {
    setShowAddToSequenceReportModal(true);
  };

  const sendAddToSequenceRequest = (selectedSequenceId, stepId) => {
    const ids = Array.from(new Set([selectedProspectIdFromState]));
    sendAddContactToStepRequest({
      stepId,
      sequenceId: selectedSequenceId,
      contactIds: ids,
    });
  };

  // Helper function to schedule debounced count API call
  const scheduleCountApiCall = () => {
    // Clear any existing timer
    if (countApiTimerRef?.current) {
      clearTimeout(countApiTimerRef.current);
    }

    // Set new timer for 20 seconds
    countApiTimerRef.current = setTimeout(() => {
      const paramsFromLocal = buildTasksStatusCountParamsFromLocalStorage(
        sortKey,
        sortOrder,
      );
      getTasksStatusCountRequest(paramsFromLocal);
      countApiTimerRef.current = null;
    }, 20000); // 20 seconds
  };

  // Helper function to clear the count API timer
  const clearCountApiTimer = () => {
    if (countApiTimerRef.current) {
      clearTimeout(countApiTimerRef.current);
      countApiTimerRef.current = null;
    }
  };

  // Helper function to update task status counts locally
  const updateTaskStatusCountsLocally = (
    action: TaskAction,
    taskCount: number,
    snoozeDateParam?: Date,
    isTaskerModalAction = false,
  ) => {
    if (!localTaskStatusCount || taskCount === 0) return;

    // If from tasker modal, only decrease current tab count
    if (isTaskerModalAction) {
      const updatedCounts = { ...localTaskStatusCount };
      if (taskStatus && updatedCounts[taskStatus] !== undefined) {
        updatedCounts[taskStatus] = Math.max(
          0,
          Number(updatedCounts[taskStatus]) - taskCount,
        );
        setLocalTaskStatusCount(updatedCounts);
        if (updateLocalTaskStatusCounts) {
          updateLocalTaskStatusCounts(updatedCounts);
        }
        setActiveTabCount(updatedCounts?.[taskStatus]?.toString());
      }
      return;
    }

    const updatedCounts = { ...localTaskStatusCount };

    // Determine source and destination status based on current tab and action
    let sourceStatus: TaskStatusKey = null;

    // Source status is the current tab (where the action was performed)
    sourceStatus = taskStatus;

    // Only decrease count in source status, no change in destination status
    if (sourceStatus) {
      if (updatedCounts[sourceStatus] !== undefined) {
        updatedCounts[sourceStatus] = Math.max(
          0,
          Number(updatedCounts[sourceStatus]) - taskCount,
        );
      }

      setLocalTaskStatusCount(updatedCounts);
      if (updateLocalTaskStatusCounts) {
        updateLocalTaskStatusCounts(updatedCounts);
      }
      if (sourceStatus === taskStatus) {
        setActiveTabCount(updatedCounts[sourceStatus]?.toString());
      }
    }
  };

  // Helper function to reverse task status counts for undo operations
  const reverseTaskStatusCountsLocally = (
    action: TaskAction,
    originalSourceStatus: TaskStatusKey,
    taskCount: number,
    snoozeDateParam?: Date,
    isTaskerModalAction = false,
  ) => {
    if (!localTaskStatusCount || taskCount === 0) {
      return;
    }

    // If from tasker modal, only increase current tab count
    if (isTaskerModalAction) {
      const updatedCounts = { ...localTaskStatusCount };
      if (taskStatus && updatedCounts[taskStatus] !== undefined) {
        updatedCounts[taskStatus] =
          Number(updatedCounts[taskStatus]) + taskCount;
        setLocalTaskStatusCount(updatedCounts);
        if (updateLocalTaskStatusCounts) {
          updateLocalTaskStatusCounts(updatedCounts);
        }
        setActiveTabCount(updatedCounts[taskStatus]?.toString());
      }
      return;
    }

    const updatedCounts = { ...localTaskStatusCount };

    // Only increase count in source status, no change in destination status
    if (originalSourceStatus) {
      if (updatedCounts[originalSourceStatus] !== undefined) {
        updatedCounts[originalSourceStatus] =
          Number(updatedCounts[originalSourceStatus]) + taskCount;
      }
      setLocalTaskStatusCount(updatedCounts);
      if (updateLocalTaskStatusCounts) {
        updateLocalTaskStatusCounts(updatedCounts);
      }
      if (originalSourceStatus === taskStatus) {
        setActiveTabCount(updatedCounts[originalSourceStatus]?.toString());
      }
    }
  };

  //! useEffect
  useEffect(() => {
    executeOnRequestStatus({
      status: taskActionRequestStatus,
      onSuccess: () => {
        // For RetryLinkedInTask, show toast and refresh list without removing the task
        if (taskAction === TaskAction.RetryLinkedInTask) {
          toaster.success('LinkedIn tasks retried successfully', {
            theme: Theme.New,
          });
          getTasksRequest({
            status: getTaskStatusParams(taskStatus),
            pageLimit: paginationOptions.pageLimit,
            pageNumber: paginationOptions.pageNumber,
            ...params,
          });
          setTaskId(null);
          setTaskType(null);
          setTaskAction(null);
          resetSelectedTasks();
          clearTasksSelection();
          return;
        }

        // Check if this is called from tasker modal (when taskId is set and modal is visible)
        const isFromTaskerModal = isTaskerModalVisible && taskId;

        // For tasker modal actions, always treat as single task action
        const isSingleTaskAction =
          isFromTaskerModal ||
          selectedTasks.length === 1 ||
          (taskId && selectedTasks.length === 0);

        if (isSingleTaskAction) {
          // For tasker modal actions, use the modal's taskId
          const taskIdToUse = isFromTaskerModal
            ? taskId
            : selectedTasks?.[0]?.id || taskId;

          // Store task information before removing it from local state
          const taskToStore =
            tasksData.data?.find((task) => task.id === taskIdToUse) ||
            (isFromTaskerModal ? null : selectedTasks[0]);

          // For tasker modal actions or hover state actions (when taskId is set but no selected tasks),
          // we should always show undo for hover actions since we have the taskId
          const shouldShowUndo =
            taskAction !== TaskAction.RetryLinkedInTask &&
            (taskToStore ||
              isFromTaskerModal ||
              (taskId && selectedTasks?.length === 0));

          if (shouldShowUndo) {
            // If have taskToStore, use it otherwise create a minimal task object for undo
            const taskForUndo = taskToStore || { id: taskIdToUse };

            const originalIndex =
              tasksData.data?.findIndex((task) => task.id === taskIdToUse) ??
              -1;

            const undoDataWithIndex = {
              task: taskForUndo,
              originalIndex: originalIndex >= 0 ? originalIndex : 0,
              originalAction: taskAction,
              originalSourceStatus: taskStatus,
              ...(taskAction === TaskAction.SnoozeTask &&
                snoozeDate && { snoozeDate }),
            };

            StorageUtil.setItem('undoTask', JSON.stringify(undoDataWithIndex));
            toaster.success(getToasterMessage(taskAction), {
              theme: Theme.New,
              showUndo: true,
              delay: 5000,
              undoAction: () => {
                const undoData = JSON.parse(StorageUtil.getItem('undoTask'));
                if (undoDataWithIndex?.task) {
                  sendGetUndoTasksRequest(params, undoData.task.id);
                }
              },
            });
          } else {
            toaster.success(getToasterMessage(taskAction), {
              theme: Theme.New,
            });
          }

          // Remove the task from local state for single task actions
          if (taskIdToUse && localTasks) {
            const updatedData = localTasks.filter(
              (task) => task.id !== taskIdToUse,
            );
            setLocalTasks(updatedData);
          }

          // If from tasker modal, update count only for current tab
          if (isFromTaskerModal) {
            updateTaskStatusCountsLocally(taskAction, 1, undefined, true);
          } else {
            // Update task status counts locally for single task actions
            updateTaskStatusCountsLocally(
              taskAction,
              1,
              taskAction === TaskAction.SnoozeTask ? snoozeDate : undefined,
            );
          }

          // Remove task from tasker data only after API success
          if (isFromTaskerModal && taskId) {
            removeTaskFromTaskerData(taskId);
          }

          // Schedule debounced count API call for single task actions (but not for tasker modal actions)
          if (!isFromTaskerModal) {
            scheduleCountApiCall();
          }
        } else {
          toaster.success(getToasterMessage(taskAction), { theme: Theme.New });
          // Only call count API immediately for bulk actions, not single task actions
          getTasksStatusCountRequest({
            ...params,
          });
        }
        setIsSnoozeConfirmationModalVisible(false);
        setIsConfirmationModalVisible(false);
        setIsReassignModalVisible(false);
        setTaskId(null);
        setTaskType(null);
        setTaskAction(null);
        setCallOutcome(null);
        setSnoozeDate(null);
        resetSelectedTasks();
        clearTasksSelection();
        // setShouldCheckCountOnTabChange(true);
      },
    });
  }, [taskActionRequestStatus]);

  useEffect(() => {
    executeOnRequestStatus({
      status: outcomeChangeRequestStatus,
      onSuccess: () => {
        if (!isTaskerModalVisible && outcomeChangePayload) {
          // Check if outcome filter is applied
          const isOutcomeFilterApplied =
            filters?.outcomes && filters.outcomes.length > 0;

          if (isOutcomeFilterApplied) {
            // If outcome filter is applied, call listing and status-count APIs
            getTasksRequest({
              status: getTaskStatusParams(taskStatus),
              pageLimit: paginationOptions.pageLimit,
              pageNumber: paginationOptions.pageNumber,
              ...params,
            });
            getTasksStatusCountRequest({ ...params });
          }

          if (!isOutcomeFilterApplied && outcomeChangePayload && localTasks) {
            const updatedData = localTasks.map((task) => {
              if (
                task?.sequence?.id === outcomeChangePayload.sequenceId &&
                task?.prospect?.id === outcomeChangePayload.prospectId
              ) {
                const outcomeDetails = allOutcomes?.find(
                  (outcome) => outcome.id === outcomeChangePayload.outcomeId,
                );

                if (outcomeDetails) {
                  return {
                    ...task,
                    sequenceProspectTask: {
                      ...task.sequenceProspectTask,
                      sequenceProspect: {
                        ...task.sequenceProspectTask?.sequenceProspect,
                        categoryId: outcomeChangePayload.outcomeId,
                        category: {
                          name: outcomeDetails.name,
                          sentiment: outcomeDetails.sentiment,
                        },
                      },
                    },
                  };
                }
              }
              return task;
            });

            setLocalTasks(updatedData);
          }

          // Clear the payload after updating
          setOutcomeChangePayload(null);
        }
      },
    });
  }, [
    outcomeChangeRequestStatus,
    outcomeChangePayload,
    allOutcomes,
    filters?.outcomes,
    params,
  ]);

  useEffect(() => {
    executeOnRequestStatus({
      status: bulkTasksUpdateRequestStatus,
      onSuccess: () => {
        toaster.success(bulkTasksUpdateRequestMessage, { theme: Theme.New });
        getTasksStatusCountRequest({
          ...params,
        });
        setIsSnoozeConfirmationModalVisible(false);
        setIsConfirmationModalVisible(false);
        setIsReassignModalVisible(false);
        setTaskId(null);
        setTaskType(null);
        setTaskAction(null);
        setCallOutcome(null);
        setSnoozeDate(null);
        resetSelectedTasks();
        clearTasksSelection();
        handleClearSelection(true);
        resetBulkTasksState();
      },
    });
  }, [bulkTasksUpdateRequestStatus, bulkTasksUpdateRequestMessage]);

  useEffect(() => {
    executeOnRequestStatus({
      status: getUndoTasksRequestStatus,
      onSuccess: () => {
        const undoData = JSON.parse(StorageUtil.getItem('undoTask'));

        if (undoData?.task) {
          // Update local state directly instead of using mutateTasks
          if (localTasks) {
            const newData = [...localTasks];

            // Insert the task at its original position
            const insertIndex = Math?.min(
              undoData?.originalIndex,
              newData?.length,
            );
            newData?.splice(insertIndex, 0, undoData.task);

            // Update local state with the restored task
            setLocalTasks(newData);
          }

          // Reverse the task status count changes for undo
          if (
            undoData.originalAction !== undefined &&
            undoData.originalAction !== null
          ) {
            // If from tasker modal, reverse count only for current tab
            if (isTaskerModalVisible && taskId) {
              reverseTaskStatusCountsLocally(
                undoData.originalAction,
                undoData.originalSourceStatus,
                1,
                undoData.snoozeDate ? new Date(undoData.snoozeDate) : undefined,
                true,
              );
            } else {
              // For undo, reverse the action:
              // - If original action was SkipTask, need to decrease Skipped count and increase source count
              // - If original action was ExecuteTask, need to decrease Completed count and increase source count
              reverseTaskStatusCountsLocally(
                undoData.originalAction,
                undoData.originalSourceStatus,
                1,
                undoData.snoozeDate ? new Date(undoData.snoozeDate) : undefined,
              );
            }
          }

          StorageUtil.removeItem('undoTask');
          if (!isTaskerModalVisible) {
            handleClearSelection(true);
          }
        }
        resetUndoTasksState();
      },
    });
  }, [getUndoTasksRequestStatus]);

  useEffect(() => {
    // Handle initial render - only call API once
    if (isInitialRenderRef.current) {
      setPaginationOptions({
        pageLimit: constants.DEFAULT_PAGE_SIZE,
        pageNumber: 1,
      });
      getTasksRequest({
        status: getTaskStatusParams(taskStatus),
        pageLimit: constants.DEFAULT_PAGE_SIZE,
        pageNumber: 1,
        ...params,
      });
      getTasksStatusCountRequest({ ...params });
      tableRef.current?.resetPagination();
      setIsBulkTasksSelected(false);
      resetSelectedTasks();
      clearTasksSelection();
      tableRef.current?.resetSelectedRows();
      isInitialRenderRef.current = false;
      prevTaskStatusRef.current = taskStatus;
      return;
    }

    // Handle tasksFilters changes
    const hasFiltersChanged = Object.keys(tasksFilters).some((key) => {
      const prevValue = prevTasksFiltersRef.current?.[key];
      const currentValue = tasksFilters[key];

      if (Array.isArray(prevValue) && Array.isArray(currentValue)) {
        return (
          prevValue.length !== currentValue.length ||
          prevValue.some((item, index) => item !== currentValue[index])
        );
      }

      // Compare other values
      return prevValue !== currentValue;
    });

    if (hasFiltersChanged) {
      getTasksRequest({
        status: getTaskStatusParams(taskStatus),
        pageLimit: paginationOptions.pageLimit,
        pageNumber: (paginationOptions.pageNumber = 1),
        ...params,
      });
      getTasksStatusCountRequest({ ...params });
      tableRef?.current?.resetPagination();
    }

    // Update refs for next comparison
    prevTasksFiltersRef.current = { ...tasksFilters };
  }, [tasksFilters]);

  useEffect(() => {
    // Skip initial render since it's handled in the tasksFilters useEffect
    if (isInitialRenderRef.current) {
      return;
    }

    // Handle taskStatus changes (tab changes)
    if (prevTaskStatusRef.current !== taskStatus) {
      setPaginationOptions({
        pageLimit: constants.DEFAULT_PAGE_SIZE,
        pageNumber: 1,
      });
      getTasksRequest({
        status: getTaskStatusParams(taskStatus),
        pageLimit: constants.DEFAULT_PAGE_SIZE,
        pageNumber: 1,
        ...params,
      });
      tableRef.current?.resetPagination();
      setIsBulkTasksSelected(false);
      resetSelectedTasks();
      clearTasksSelection();
      tableRef.current?.resetSelectedRows();

      // Update activeTabCount immediately when tab changes
      const effectiveTaskStatusCount = localTaskStatusCount || taskStatusCount;
      if (effectiveTaskStatusCount && effectiveTaskStatusCount[taskStatus]) {
        setActiveTabCount(effectiveTaskStatusCount[taskStatus]?.toString());
      }

      prevTaskStatusRef.current = taskStatus;
    }
  }, [taskStatus, localTaskStatusCount, taskStatusCount]);

  useEffect(() => {
    sendGetAllOutcomesRequest();
  }, []);

  // Update local tasks when tasks prop changes
  useEffect(() => {
    setLocalTasks(tasks);
  }, [tasks]);

  useEffect(() => {
    if (sortKey && sortOrder) {
      getTasksRequest({
        status: getTaskStatusParams(taskStatus),
        pageLimit: paginationOptions.pageLimit,
        pageNumber: paginationOptions.pageNumber,
        ...params,
      });
    }
  }, [sortKey, sortOrder]);

  // Update local task status counts when taskStatusCount prop changes
  useEffect(() => {
    setLocalTaskStatusCount({ ...(taskStatusCount || []) });
    if (updateLocalTaskStatusCounts) {
      updateLocalTaskStatusCounts(taskStatusCount);
    }
  }, [taskStatusCount]);

  // Update activeTabCount when localTaskStatusCount changes
  useEffect(() => {
    if (
      localTaskStatusCount &&
      localTaskStatusCount[taskStatus] !== undefined
    ) {
      setActiveTabCount(localTaskStatusCount[taskStatus]?.toString());
    }
  }, [localTaskStatusCount, taskStatus]);

  // Removed redundant useEffect that was causing duplicate API calls on initial render

  useEffect(() => {
    const queryParams = new URLSearchParams(window?.location?.search);
    const statusFromUrl = queryParams?.get('status') || TaskStatusKey.DueToday;

    // Use local task status counts if available, otherwise fall back to Redux state
    const effectiveTaskStatusCount = localTaskStatusCount || taskStatusCount;

    if (effectiveTaskStatusCount && statusFromUrl) {
      setActiveTabCount(effectiveTaskStatusCount[statusFromUrl]);
    }
  }, [localTaskStatusCount, taskStatusCount, window?.location?.search]);

  useEffect(
    () => () => {
      hideLoadingBar();
      resetSelectedTasks();
      // Clear the count API timer on component unmount
      clearCountApiTimer();
    },
    [],
  );

  useEffect(() => {
    if (bulkSelectedTasks?.isAllTasksSelected && tasksData?.data?.length > 0) {
      // Only select all current page rows when within the 25,000 limit
      if (shouldSelectAllCurrentPageRows()) {
        tableRef.current?.selectAllCurrentPageRows();
      } else {
        tableRef.current?.resetSelectedRows();
      }
    }
  }, [bulkSelectedTasks?.isAllTasksSelected, tasksData?.data]);

  useEffect(() => {
    const handlePopState = () => {
      const queryParams = new URLSearchParams(window?.location?.search);
      const hashId = queryParams?.get('taskId');
      if (hashId) {
        setTaskerModalVisible(true);
        setShareableHashId(hashId);
      } else {
        setTaskerModalVisible(false);
        setShareableHashId(null);
      }
    };

    window?.addEventListener('popstate', handlePopState);

    handlePopState();

    return () => {
      window?.removeEventListener('popstate', handlePopState);
    };
  }, []);

  // Prospect Details useEffect hooks
  useEffect(() => {
    executeOnRequestStatus({
      status: addToStepRequestStatus,
      onSuccess: () => {
        hideAddToSequenceModal();
        resetSelectedTasks();
        onShowAddToSequenceReportModal();
        handleClearSelection();
        resetSequenceAndStepData();
      },
      onFailed: () => {
        hideAddToSequenceModal();
        resetSelectedTasks();
        resetSequenceAndStepData();
        toaster.error(addToStepRequestError?.message);
      },
    });
  }, [addToStepRequestStatus]);

  useEffect(() => {
    executeOnRequestStatus({
      status: addTagsToProspectsRequestStatus,
      onSuccess: () => {
        sendGetSingleProspectDetailsRequest(selectedProspectIdFromState);
      },
    });
  }, [addTagsToProspectsRequestStatus]);

  useEffect(() => {
    executeOnRequestStatus({
      status: saveContactAttributesRequestStatus,
      onSuccess: () => {
        // Set flag to refresh tasks when modal is hidden
        setShouldRefreshTasksAfterModalHide(true);
      },
    });
  }, [saveContactAttributesRequestStatus]);

  useEffect(() => {
    if (showAddToSequenceModal) {
      sendGetSequenceList();
    }
  }, [showAddToSequenceModal]);

  useEffect(() => {
    // Skip on initial render
    if (isFirstTabChange?.current) {
      isFirstTabChange.current = false;
      prevSearchValueRef.current = search;
      confirmedSearchValueRef.current = search;
      return;
    }
    // Only run on tab change if search is different from last search
    if (search !== confirmedSearchValueRef?.current) {
      getTasksStatusCountRequest({ ...params });
      confirmedSearchValueRef.current = search;
    }
    prevSearchValueRef.current = search;
  }, [taskStatus]);

  //! Render Function
  const getModalContent = () => {
    // Check if this is called from tasker modal (when taskId is set and modal is visible)
    const isFromTaskerModal = isTaskerModalVisible && taskId;

    // For tasker modal actions, always treat as single task
    const effectiveSelectedTasks = isFromTaskerModal ? [] : selectedTasks;
    const effectiveSelectedCount = isFromTaskerModal
      ? 1
      : selectedTasks?.length;

    if (taskAction === TaskAction.ExecuteTask) {
      return (
        <>
          {isCallTask(taskType) ||
          isAnyCallTaskSelected(effectiveSelectedTasks) > 0 ? (
            <>
              <div className="d-flex flex-column gap-16">
                <div className="regular-2 popover-arrow-color-txt line-height-24">
                  <span>You're about to mark task as complete.</span>
                </div>
                <span className="regular-2 popover-arrow-color-txt complete-task-description line-height-24">
                  {getCallTaskDesc(effectiveSelectedCount)}
                </span>
              </div>
              <div className="mt-3">
                <Select<{ key: string; label: string }>
                  options={getCallOutcomeOptions}
                  placeholder="Select call task outcome"
                  selectedOptionKey={callOutcome?.key}
                  onChange={([option]) => setCallOutcome(option)}
                  optionRenderer={(option) => <span>{option?.label}</span>}
                  selectedOptionRenderer={([option]) => (
                    <span>{option?.label}</span>
                  )}
                />
              </div>
            </>
          ) : (
            <span className="regular-2 popover-arrow-color-txt mb-3">
              {getCompleteActionDesc(effectiveSelectedCount)}
            </span>
          )}
        </>
      );
    }

    if (taskAction === TaskAction.SkipTask) {
      return effectiveSelectedCount > 1 ||
        bulkSelectedTasks?.isAllTasksSelected ? (
        <p>
          You are about to skip multiple tasks. Skipping these tasks will
          automatically move the selected prospects to the next step.
        </p>
      ) : (
        <p>Are you sure you want to skip this task?</p>
      );
    }

    return null;
  };

  const isButtonDisabled = () => {
    // Check if this is called from tasker modal (when taskId is set and modal is visible)
    const isFromTaskerModal = isTaskerModalVisible && taskId;

    // For tasker modal actions, always treat as single task
    const effectiveSelectedTasks = isFromTaskerModal ? [] : selectedTasks;

    return (
      getIsRequestPending(taskActionRequestStatus) ||
      getIsRequestPending(bulkTasksUpdateRequestStatus) ||
      (!callOutcome &&
        (isCallTask(taskType) ||
          isAnyCallTaskSelected(effectiveSelectedTasks) > 0) &&
        taskAction !== TaskAction.SkipTask)
    );
  };

  const isLoading = getIsRequestPending(getTasksRequestStatus);
  const displayCount = Number(activeTabCount);

  useEffect(() => {
    if (isLoading) {
      showLoadingBar();
    } else {
      hideLoadingBar();
    }
  }, [isLoading]);

  return (
    <>
      <div className="tasks__content">
        <>
          {isFirstTimeUser ? (
            <TasksFilterBarLoader
              status={taskStatus}
              tasksCount={Number(activeTabCount)}
            />
          ) : (
            <TasksFilterBar
              onFilterToggle={toggleFilter}
              searchValue={search}
              setSearchValue={setSearch}
              onSearch={(searchValue: string) => handleSearch(searchValue)}
              onClearSearch={handleClearSearch}
              onTaskTypeFilterApply={(selectedIds: number[]) =>
                onTaskTypeFilterApply(selectedIds)
              }
              onRefresh={onRefresh}
              onAction={(action: TaskAction) => onAction(action)}
              onStartAllTasks={() => {
                resetSelectedTasks();
                clearTasksSelection();
                setTaskerModalVisible(true);
              }}
              tasksCount={Number(activeTabCount)}
              status={taskStatus}
              bulkAction={bulkSelectedTasks?.isAllTasksSelected}
              hasSelectedTasks={
                selectedTasks.length > 0 ||
                (bulkSelectedTasks?.isAllTasksSelected &&
                  getLimitedTaskCount() -
                    (bulkSelectedTasks.deselectedTaskIds?.length || 0) >
                    0)
              }
              selectedTaskCount={
                bulkSelectedTasks?.isAllTasksSelected
                  ? getLimitedTaskCount() -
                    (bulkSelectedTasks.deselectedTaskIds?.length || 0)
                  : getFilteredSelectedTasksCount()
              }
              isSingleLinkedInAutomationTask={
                selectedTasks.length === 1 &&
                (selectedTasks[0]?.taskTitle?.type ===
                  SequenceStepType.AimfoxCampaignPush ||
                  selectedTasks[0]?.taskTitle?.type ===
                    SequenceStepType.HeyReachCampaignPush)
              }
            />
          )}

          {checkIsFilterApplied(tasksFilters) && (
            <div className="mt-4 bs-mb-20 pl-4 pr-4 tasks-filter">
              <ProspectsFilterBar
                prospectsFilters={tasksFilters}
                resetProspectsFilters={resetTasksFilters}
                getProspects={getTasksAfterFilter}
              />
            </div>
          )}

          {getShouldShowSelectedRowInfoBanner(
            selectedTasks,
            bulkSelectedTasks,
            isBulkTasksSelected || bulkSelectedTasks?.isAllTasksSelected,
          ) && (
            <div className="tasks-banner">
              <TasksSelectedRowInfoBanner
                selectedTasks={selectedTasks}
                bulkSelectedTasks={bulkSelectedTasks}
                totalTasks={Number(activeTabCount)}
                handleSelectAllTasks={handleSelectAllProspect}
                handleClearSelection={() => handleClearSelection(true)}
                currentFilters={filters}
                currentTasks={tasksData?.data}
              />
            </div>
          )}

          {!isLoading && tasksData?.data?.length === 0 && !isFirstTimeUser ? (
            <EmptyList activeTab={taskStatus} />
          ) : (
            <div
              className={`tasks__content-table-list ${
                isFirstTimeUser ? 'scroll-behaviour-task' : ''
              } ${getTaskTableClass(tasksFilters, selectedTasks?.length)} ${
                isDemoAccount ? 'isDemoAccount' : ''
              } ${isFirstTimeUser ? 'opacity-40' : ''} ${
                isCompletedOrSkippedTab(taskStatus)
                  ? 'is-completed-or-skipped'
                  : ''
              } ${isFailedTab ? 'is-failed-tab' : ''}`}
            >
              <Table
                columns={getTasksColumns(
                  taskStatus,
                  updateTasks,
                  onTaskClick,
                  onSort,
                  selectedTasks,
                  copiedRows,
                  setCopiedRows,
                  viewShowProspectDetailsModal,
                  isFirstTimeUser,
                  allOutcomes,
                  {
                    selectedTasks,
                    bulkSelectedTasks,
                    singleSelected,
                    allSelected,
                    tasksData: tasksData.data || [],
                    pageNumber: paginationOptions.pageNumber,
                    pageLimit: paginationOptions.pageLimit,
                  },
                  hoveredRowId,
                  handleRowMouseEnter,
                  handleRowMouseLeave,
                  hoveredRowRef,
                  (payload) => setOutcomeChangePayload(payload),
                  isFailedTab
                    ? (row: FailedTaskDetail) => setFailedReasonTask(row as any)
                    : undefined,
                )}
                data={tasksTableData(tasksData.data)}
                onPaginationOptionsChange={paginationChange}
                paginationOptions={{
                  options: {
                    limit: paginationOptions.pageLimit,
                    page: paginationOptions.pageNumber,
                    totalElements: displayCount,
                  },
                }}
                pagination={PaginationShowHide.SHOW}
                isNewPagination={true}
                headerWrapperClasses="tasks-table-header"
                bodyWrapperClasses="tasks-body-wrapper"
                isLoading={isLoading || isFirstTimeUser}
                tableRef={(refValue: any) => (tableRef.current = refValue)}
                useCustomSortIcon={true}
                loadingRowsCount={isFirstTimeUser ? 12 : 4}
                formattedTotalDisplay={formatNumberWithCommas(displayCount)}
                useFormattedTotalDisplay={true}
                customPageLimit={true}
                customPaginationOptions={showingLengthPerPageOptions}
              />
            </div>
          )}

          {isFirstTimeUser && !isLoading && (
            <div>
              <TaskEmptyList />
            </div>
          )}
        </>
      </div>

      {failedReasonTask && (
        <FailedReasonPopup
          task={failedReasonTask}
          show={!!failedReasonTask}
          onClose={() => setFailedReasonTask(null)}
          onRefresh={onRefresh}
        />
      )}

      <BulkRetryModal
        show={showBulkRetryModal}
        isRetrying={isBulkRetrying}
        onConfirm={handleBulkRetry}
        onClose={() => setShowBulkRetryModal(false)}
      />

      {isSnoozeConfirmationModalVisible && (
        <SnoozeModal
          show={isSnoozeConfirmationModalVisible}
          onClose={() => {
            setIsSnoozeConfirmationModalVisible(false);
            setTaskId(null);
            setTaskType(null);
            setTaskAction(null);
            setCallOutcome(null);
            setSnoozeDate(null);
          }}
          onSubmit={(date: Date) => onSnooze(date)}
          isRequestPending={
            getIsRequestPending(taskActionRequestStatus) ||
            getIsRequestPending(bulkTasksUpdateRequestStatus)
          }
          bulkAction={
            isTaskerModalVisible && taskId
              ? false
              : selectedTasks?.length > 1 ||
                bulkSelectedTasks?.isAllTasksSelected
          }
        />
      )}
      {isConfirmationModalVisible && (
        <ConfirmationModalV3
          show={isConfirmationModalVisible}
          onSubmit={onConfirm}
          onClose={() => {
            setIsConfirmationModalVisible(false);
            setTaskId(null);
            setTaskType(null);
            setTaskAction(null);
            setCallOutcome(null);
            setSnoozeDate(null);
          }}
          showCloseIcon={false}
          hideTitleIcon={true}
          icon={
            taskAction === TaskAction.ExecuteTask ? (
              <CircleCheck width={24} height={24} className="orange-txt-14" />
            ) : (
              <ForwardStep width={24} height={24} className="orange-txt-14" />
            )
          }
          title={getTaskActionTitle(
            taskAction,
            // eslint-disable-next-line no-nested-ternary
            isTaskerModalVisible && taskId
              ? 1
              : bulkSelectedTasks?.isAllTasksSelected
              ? getLimitedTaskCount() -
                (bulkSelectedTasks.deselectedTaskIds?.length || 0)
              : getFilteredSelectedTasksCount(),
          )}
          cancelButtonText="No"
          submitButtonText={`${
            taskAction === TaskAction.SkipTask ? 'Skip Anyway' : 'Yes'
          }`}
          content={getModalContent()}
          isSubmitLoading={
            getIsRequestPending(taskActionRequestStatus) ||
            getIsRequestPending(bulkTasksUpdateRequestStatus)
          }
          isSubmitDisabled={isButtonDisabled()}
          className={`task-confirmation-modal ${getConfirmationModalClass(
            taskAction,
            taskType,
            isTaskerModalVisible && taskId ? [] : selectedTasks,
          )}`}
        />
      )}

      {isTaskFilterModalVisible && (
        <ProspectsFilters
          show={isTaskFilterModalVisible}
          hide={hideFilterModal}
          getProspects={getTasksAfterFilter}
          resetProspectsFilters={resetTasksFilters}
          setProspectsFilters={setTasksFilters}
          prospectsFilters={tasksFilters}
          except={[
            Filters.ConversationType,
            Filters.Owners,
            Filters.Tags,
            Filters.VerificationStatus,
            Filters.CreationSource,
            Filters.Status,
            Filters.ClientAssociated,
            Filters.EmailAccount,
            Filters.SubSequenceStepIds,
            Filters.CurrentStep,
            Filters.ImportedFile,
          ]}
          isATMOwnerList={true}
          isTasksFilter={true}
          handleClearAll={onClearFilter}
          taskTab={true}
        />
      )}

      {isReassignModalVisible && (
        <ReassignModal
          show={isReassignModalVisible}
          onClose={() => {
            setIsReassignModalVisible(false);
            setTaskId(null);
            setTaskType(null);
            setTaskAction(null);
            setCallOutcome(null);
            setSnoozeDate(null);
          }}
          onSubmit={(id: number) => onReassign(id)}
        />
      )}

      {isTaskerModalVisible && (
        <TaskerModal
          show={isTaskerModalVisible}
          onHide={() => {
            setTaskerModalVisible(false);
            setShareableHashId(null);
            // Clean URL - always remove hashId parameter when modal is closed
            const queryParams = new URLSearchParams(window.location.search);
            queryParams.delete('taskId');
            const newSearch = queryParams.toString();
            const { pathname } = window.location;
            window.history.replaceState(
              null,
              '',
              `${pathname}${newSearch ? `?${newSearch}` : ''}`,
            );
            if (taskerModalUserInteractedRef.current) {
              getTasksRequest({
                status: getTaskStatusParams(taskStatus),
                pageLimit: paginationOptions.pageLimit,
                pageNumber: paginationOptions.pageNumber,
                ...params,
              });
              getTasksStatusCountRequest({ ...params });
            }
            taskerModalUserInteractedRef.current = false;
          }}
          taskStatus={taskStatus}
          tasksCount={Number(activeTabCount)}
          paginationOptions={paginationOptions}
          taskId={taskId}
          onOutcomeChange={(outcome) => setCallOutcome(outcome)}
          updateTasks={(
            tId: number,
            tAction: TaskAction,
            tType: SequenceStepType,
            ePayload?: any,
          ) => updateTasks(tId, tAction, tType, ePayload, false)}
          filters={params}
          resetTaskId={() => setTaskId(null)}
          onRefresh={onRefresh}
          setShowGlobalRestrictionModal={(tType: SequenceStepType) => {
            let content = {
              title: 'Viewing LinkedIn profiles is disabled in demo mode',
              description:
                'This is a demo account. Signup to perform tasks in real account.',
            };

            if (tType === SequenceStepType.LinkedInConnectionRequest) {
              content = {
                title: 'Send Connection Request',
                description:
                  'This is a demo account with sample data. Signup to perform tasks in real account.',
              };
            }
            if (tType === SequenceStepType.LinkedInMessage) {
              content = {
                title: 'Sending Message is disabled in demo mode',
                description:
                  'This is a demo account. Signup to perform tasks in real account.',
              };
            }
            if (tType === SequenceStepType.LinkedInInMail) {
              content = {
                title: 'Sending InMail is disabled in demo mode',
                description:
                  'This is a demo account. Signup to perform tasks in real account.',
              };
            }

            setGlobalRestrictionModalContent(content);
            setShowGlobalRestrictionModal(true);
          }}
          shareableHashId={shareableHashId}
          setUserInteractedRef={taskerModalUserInteractedRef}
          taskOpenProspectId={taskOpenProspectId}
        />
      )}

      <GlobalRestrictionModal
        show={showGlobalRestrictionModal}
        onClose={() => setShowGlobalRestrictionModal(false)}
        title={globalRestrictionModalContent.title}
        content={globalRestrictionModalContent.description}
        primaryBtnLabel="Sign up"
      />

      {/* Prospect Details Modals */}
      {showProspectDetailsModal && selectedProspectIdFromState && (
        <ProspectDetails
          show={showProspectDetailsModal}
          onHide={hideShowProspectDetailsModal}
          selectedProspectId={selectedProspectIdFromState}
          resetSelectedProspectId={resetSelectedId}
          selectedProspectData={selectedProspectDataFromState}
          selectedProspectSequences={selectedProspectSequencesFromState}
          getSingleProspectDetailsStatus={getSingleProspectDetailsStatus}
          getSingleProspectSequencesDetailsStatus={
            getSingleProspectSequencesDetailsStatus
          }
          isTagsFilterApplied={false}
          currentSequenceId={null}
          defaultTab={defaultTab || 'contacts'}
        />
      )}

      {showAddToSequenceModal && (
        <AddToSequenceModal
          show={showAddToSequenceModal}
          onHide={hideAddToSequenceModal}
          addToSequence={sendAddToSequenceRequest}
          sequence={null}
          isSubscribed={true}
          currentSequenceId={null}
        />
      )}

      {prospectActivityModalState.showEngagementStatsModal && (
        <ProspectActivityModal
          show={prospectActivityModalState.showEngagementStatsModal}
          onHide={hideEngagementStatisticsPopover}
          prospectFullName={prospectActivityModalState.prospectName}
          prospectEmail={prospectActivityModalState.prospectEmail}
          timezone="UTC"
          sequenceId={prospectActivityModalState.activeProspectId}
          prospectId={prospectActivityModalState.activeProspectId}
          type={prospectActivityModalState.activeType}
          isSubsequence={false}
        />
      )}
    </>
  );
};

export default TasksContent;
